
////////////////////////////////////////////// Main-Code

import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, ChevronDown, X, Loader2, ImageOff, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getVendorCapabilityData, getVendorCapabilityImage } from "../../../services/apiService";
import Swal from "sweetalert2";

// Constants
const CATEGORY = "Vendor Capability";
const SUB_CATEGORY = "hardware";
const PRODUCT_TYPE = "PD_EPD";

// Dynamic column width calculator
const COL_KEYS = [
  "region",
  "testCategory",
  "tpvTestItems",
  "tpvTestCondition",
  "tpvTestStandard",
  "chineseStandard",
  "odmBrand",
  "testItem",
  "testStandard",
  "standardComparison",
];

const COL_CONSTRAINTS = {
  region: { min: 70, max: 120 },
  testCategory: { min: 140, max: 300 },
  tpvTestItems: { min: 140, max: 300 },
  tpvTestCondition: { min: 180, max: 450 },
  tpvTestStandard: { min: 180, max: 450 },
  chineseStandard: { min: 100, max: 200 },
  odmBrand: { min: 120, max: 200 },
  testItem: { min: 140, max: 300 },
  testStandard: { min: 200, max: 500 },
  standardComparison: { min: 120, max: 240 },
};

function computeColumnWidths(rows) {
  const maxLen = {
    region: 10,
    testCategory: 15,
    tpvTestItems: 15,
    tpvTestCondition: 20,
    tpvTestStandard: 20,
    chineseStandard: 16,
    odmBrand: 15,
    testItem: 15,
    testStandard: 20,
    standardComparison: 20,
  };

  if (rows && rows.length > 0) {
    rows.forEach((row) => {
      const getLen = (val, imgKeys) => {
        let textLen = 0;
        if (val) {
          const str = String(val).replace(/Reference picture hyperlink_\d+/gi, "").trim();
          for (let lang = 0; lang < str.length; lang++) {
            textLen += str.charCodeAt(lang) > 255 ? 1.8 : 1;
          }
        }
        let imgLen = (imgKeys && imgKeys.length > 0) ? 27 : 0;
        return Math.max(textLen, imgLen);
      };

      maxLen.region = Math.max(maxLen.region, getLen(row.region));
      maxLen.testCategory = Math.max(maxLen.testCategory, getLen(row.testCategory));
      maxLen.tpvTestItems = Math.max(maxLen.tpvTestItems, getLen(row.tpvTestItems));
      maxLen.tpvTestCondition = Math.max(maxLen.tpvTestCondition, getLen(row.tpvTestCondition, row.tpvConditionImageKeys));
      maxLen.tpvTestStandard = Math.max(maxLen.tpvTestStandard, getLen(row.tpvTestStandard, row.tpvStandardImageKeys));
      maxLen.chineseStandard = Math.max(maxLen.chineseStandard, getLen(row.chineseStandard));
      maxLen.odmBrand = Math.max(maxLen.odmBrand, getLen(row.odmBrand));
      maxLen.testItem = Math.max(maxLen.testItem, getLen(row.testItem));
      maxLen.testStandard = Math.max(maxLen.testStandard, getLen(row.testStandard, row.imageKeys));
      maxLen.standardComparison = Math.max(maxLen.standardComparison, getLen(row.standardComparison));
    });
  }

  const pxFromChars = (len) => {
    const PADDING = 32;
    if (len <= 30) return Math.round(len * 7.5 + PADDING);
    return Math.round((30 * 7.5) + PADDING + ((len - 30) * 2.5));
  };

  return Object.fromEntries(
    COL_KEYS.map((k) => {
      const raw = pxFromChars(maxLen[k]);
      const { min, max } = COL_CONSTRAINTS[k];
      const clamped = Math.max(min, Math.min(max, raw));
      return [k, clamped];
    })
  );
}

// ImageModal component (unchanged)
function ImageModal({ imageKey, onClose }) {
  const [imgData, setImgData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!imageKey) return;
    setLoading(true);
    setError(null);
    setImgData(null);
    setNaturalSize({ width: 0, height: 0 });

    getVendorCapabilityImage(imageKey, {
      category: CATEGORY,
      sub_category: SUB_CATEGORY,
      productType: PRODUCT_TYPE,
    })
      .then((data) => {
        if (data.success) {
          setImgData(data);
          const img = new Image();
          img.src = `data:${data.mimeType};base64,${data.base64}`;
          img.onload = () => {
            setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
          };
          img.onerror = () => setNaturalSize({ width: 1200, height: 800 });
        } else {
          setError(data.message || "Image not found");
        }
      })
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [imageKey]);

  if (!imageKey) return null;

  let widthClass = "w-[90vw] max-w-[500px]";
  if (naturalSize.width > 0) {
    if (naturalSize.width <= 500) widthClass = "w-[min(90vw,480px)] max-w-[480px]";
    else if (naturalSize.width <= 900) widthClass = "w-[min(90vw,820px)] max-w-[820px]";
    else if (naturalSize.width <= 1400) widthClass = "w-[min(92vw,1280px)] max-w-[1280px]";
    else widthClass = "w-[min(94vw,1600px)] max-w-[1600px]";
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md" onClick={onClose}>
      <div
        className={`relative bg-[#0d1117] border border-gray-700 ${widthClass} max-h-[94vh] mx-3 sm:mx-6 lg:mx-10 overflow-hidden shadow-2xl flex flex-col transition-all duration-300`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-1 border-b border-gray-800">
          <p className="sm:text-base text-sm font-mono text-blue-400">{imageKey}</p>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-gray-300 hover:text-white font-bold hover:bg-gray-800 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white font-bold" />
          </button>
        </div>

        <div className="flex-1 relative bg-black/30 flex items-center justify-center p-1 sm:p-1 md:p-1 overflow-hidden">
          {loading && (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
              <p className="text-gray-400">Loading...</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center gap-4 text-center">
              <ImageOff className="w-12 h-12 text-red-500" />
              <p className="text-red-400 max-w-md">{error}</p>
            </div>
          )}

          {!loading && imgData && (
            <img
              src={`data:${imgData.mimeType};base64,${imgData.base64}`}
              alt={imgData.imageName || "Preview"}
              className="max-w-full max-h-[calc(94vh-110px)] w-auto h-auto object-contain border border-gray-700/60 shadow-2xl select-none pointer-events-none"
              draggable={false}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// CellWithImages component (unchanged)
function CellWithImages({ text, imageKeys, onImageClick }) {
  const textOnly = (text || "").replace(/Reference picture hyperlink_\d+/gi, "").trim();
  const hasContent = textOnly || (imageKeys && imageKeys.length > 0);
  if (!hasContent) return <span className="text-gray-600">—</span>;

  const handleClick = async (key) => {
    try {
      const data = await getVendorCapabilityImage(key, {
        category: CATEGORY,
        sub_category: SUB_CATEGORY,
        productType: PRODUCT_TYPE,
      });

      if (!data.success) {
        alert("Failed to load image: " + (data.message || "Unknown error"));
        return;
      }

      const isEmf =
        data.mimeType?.toLowerCase().includes("emf") ||
        data.mimeType?.toLowerCase().includes("x-emf") ||
        data.imageName?.toLowerCase().endsWith(".emf");

      if (isEmf) {
        const result = await Swal.fire({
          icon: "info",
          title: "Preview not supported",
          text: "This image format (.emf) cannot be previewed in the browser.",
          showCancelButton: true,
          confirmButtonText: "Download",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#3b82f6",
          cancelButtonColor: "red",
          background: "white",
          color: "black",
        });

        if (!result.isConfirmed) return;

        const byteCharacters = atob(data.base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.mimeType || "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = data.imageName || `reference_${key}.emf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        onImageClick(key);
      }
    } catch (err) {
      console.error("Image load error:", err);
      alert("Failed to process image: " + err.message);
    }
  };

  return (
    <div className="space-y-1.5 w-full overflow-hidden">
      {textOnly && (
        <div
          className="text-[11px] leading-relaxed text-gray-300 whitespace-pre-wrap break-words overflow-wrap-anywhere w-full"
          style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
        >
          {textOnly}
        </div>
      )}

      {imageKeys?.map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => handleClick(key)}
          className="block text-[11px] text-blue-400 hover:text-blue-200 underline underline-offset-2 decoration-blue-600/50 transition-colors text-left cursor-pointer w-full"
        >
          reference picture hyperlink
        </button>
      ))}
    </div>
  );
}

// Dropdown component (unchanged)
function Dropdown({ label, options = [], value, onChange, disabled }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={[
          "flex items-center justify-between gap-1 sm:text-base text-sm px-5 py-2.5 rounded-full w-full transition-all border",
          disabled
            ? "opacity-40 cursor-not-allowed bg-gray-500 border-gray-700 text-white"
            : value && value !== label
              ? "bg-[#1a2d45] border-blue-500 text-white cursor-pointer hover:bg-[#1e3450]"
              : "bg-[#111827] border-gray-500 text-white cursor-pointer hover:bg-[#1a2235] hover:border-gray-600",
        ].join(" ")}
      >
        <span className="truncate">{value || label}</span>
        <div className="flex items-center gap-1 shrink-0">
          {value && value !== label && !disabled && (
            <span
              onMouseDown={(e) => {
                e.stopPropagation();
                onChange("");
                setOpen(false);
              }}
              className="text-gray-200 hover:text-white cursor-pointer"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white font-bold" />
            </span>
          )}
          <ChevronDown
            className={`w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white shrink-0 transition-transform duration-200 font-bold ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {open && (
        <div className="absolute top-full mt-1.5 left-0 z-40 bg-[#0e1621] border border-gray-700 rounded-xl shadow-2xl min-w-full max-h-64 overflow-y-auto">
          {options.length === 0 ? (
            <p className="text-xs text-gray-600 px-4 py-3">No options available</p>
          ) : (
            options.map((opt) => (
              <button
                key={opt}
                type="button"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  onChange(opt);
                  setOpen(false);
                }}
                className={[
                  "w-full text-left text-sm px-4 py-2.5 transition-colors",
                  opt === "All" ? "text-gray-500 border-b border-gray-800/60" : "text-gray-300",
                  value === opt ? "bg-gray-500 text-blue-400" : "hover:bg-gray-400 hover:text-white",
                ].join(" ")}
              >
                {opt}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Main component
export default function PDEPDHardwareCapability() {
  const navigate = useNavigate();

  const [testCategory, setTestCategory] = useState("");
  const [testItem, setTestItem] = useState("");
  const [odmBrand, setOdmBrand] = useState("");
  const [filterOptions, setFilterOptions] = useState({ testCategories: [], testItems: [], odmBrands: [] });
  const [rows, setRows] = useState([]);
  const [colWidths, setColWidths] = useState(() => computeColumnWidths([]));
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [queried, setQueried] = useState(false);
  const [activeImageKey, setActiveImageKey] = useState(null);

  const loadFilters = useCallback(async () => {
    setFiltersLoading(true);
    try {
      const data = await getVendorCapabilityData(CATEGORY, SUB_CATEGORY, PRODUCT_TYPE, {
        testCategory,
        testItem,
      });
      setFilterOptions(data.filters || { testCategories: [], testItems: [], odmBrands: [] });
    }
    catch (err) {
      console.error("API Error:", err);

      // ✅ HANDLE 404 ERROR
      if (err.response?.status === 404) {
        Swal.fire({
          icon: "warning",
          title: "Template Missing",
          text: "File needs to be uploaded in Template Management",
          confirmButtonText: "OK",
          confirmButtonColor: "#2563eb",
          background: "white",
          color: "black"
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/vendor-capability", { state: { view: "hardware" } });
          }
        });

        return;
      }

      setError(err.response?.data?.message || err.message || "Failed to fetch data");
    }
    finally {
      setFiltersLoading(false);
    }
  }, [testCategory, testItem]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const handleCategoryChange = (val) => {
    setTestCategory(val);
    setTestItem("");
    setOdmBrand("");
    setQueried(false);
    setRows([]);
  };

  const handleItemChange = (val) => {
    setTestItem(val);
    setOdmBrand("");
    setQueried(false);
    setRows([]);
  };

  const handleBrandChange = (val) => {
    setOdmBrand(val);
    setQueried(false);
    setRows([]);
  };

  const canQuery = !filtersLoading && (
    (testCategory && testCategory !== "") ||
    (testItem && testItem !== "") ||
    (odmBrand && odmBrand !== "")
  );

  const handleQuery = async () => {
    if (!canQuery) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getVendorCapabilityData(CATEGORY, SUB_CATEGORY, PRODUCT_TYPE, {
        testCategory,
        testItem,
        odmBrand,
      });
      const fetchedRows = data.rows || [];
      setRows(fetchedRows);
      setColWidths(computeColumnWidths(fetchedRows));
      if (data.filters) setFilterOptions((p) => ({ ...p, ...data.filters }));
      setQueried(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const testCategoryOptions = filterOptions.testCategories;
  const testItemOptions = ["All", ...filterOptions.testItems];
  const odmBrandOptions = ["All", ...filterOptions.odmBrands];

  const groupedRows = (() => {
    if (!rows.length) return [];
    const out = [];
    let grouprow = 0;
    while (grouprow < rows.length) {
      const cur = rows[grouprow];
      let span = 1;
      while (
        grouprow + span < rows.length &&
        rows[grouprow + span].testCategory === cur.testCategory &&
        rows[grouprow + span].tpvTestItems === cur.tpvTestItems
      ) span++;
      out.push({ ...cur, _span: span, _isFirst: true });
      for (let rowspan = 1; rowspan < span; rowspan++) {
        out.push({ ...rows[grouprow + rowspan], _span: span, _isFirst: false });
      }
      grouprow += span;
    }
    return out;
  })();

  return (
    <div className="min-h-screen bg-[#07090e] text-white flex flex-col">
      {/* Sticky top section */}
      <div className="sticky top-0 z-30 shrink-0 bg-[#07090e]">
        <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-800 bg-[#0b0f16]">
          <button
            onClick={() => navigate("/vendor-capability", { state: { view: "hardware" } })}
            className="p-1.5 rounded-lg hover:bg-gray-800 transition shrink-0"
          >
            <ArrowLeft className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8 text-white font-bold shrink-0" />
          </button>
          <div>
            <h1 className="text-sm sm:text-base md:text-lg lg:text-3xl text-white font-bold">
              Monitor Hardware Validation Capability
            </h1>
          </div>
        </div>

        <div className="sm:px-10 px-4 pt-3 border-b border-gray-800/50 bg-[#080c12]">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6 lg:gap-8 mb-6 w-full">
            <div className="flex flex-col w-full">
              <label className="text-sm sm:text-base md:text-lg lg:text-xl text-white px-5">Test Category</label>
              <Dropdown
                label="Test Category"
                options={testCategoryOptions}
                value={testCategory}
                onChange={handleCategoryChange}
                disabled={filtersLoading}
              />
            </div>

            <div className="flex flex-col w-full">
              <label className="text-sm sm:text-base md:text-lg lg:text-xl text-white px-5">Test Item</label>
              <Dropdown
                label="Test Item"
                options={testItemOptions}
                value={testItem}
                onChange={handleItemChange}
                disabled={!testCategory || filtersLoading}
              />
            </div>

            <div className="flex flex-col w-full">
              <label className="text-sm sm:text-base md:text-lg lg:text-xl text-white px-5">ODM/Other Brand</label>
              <Dropdown
                label="ODM/Other Brand"
                options={odmBrandOptions}
                value={odmBrand}
                onChange={handleBrandChange}
                disabled={!testItem || filtersLoading}
              />
            </div>
          </div>

          <div className="flex justify-center mb-1 px-1">
            <button
              type="button"
              onClick={handleQuery}
              disabled={!canQuery || loading}
              className={`flex items-center justify-center gap-2 w-full sm:w-auto px-6 sm:px-10 lg:px-60 sm:py-3 py-2 rounded-lg text-base sm:text-lg lg:text-xl font-semibold transition-all ${canQuery && !loading
                ? "bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/30 cursor-pointer"
                : "bg-gray-500 text-white cursor-not-allowed opacity-60"
                }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </>
              ) : (
                <>Test Standard Query</>
              )}
            </button>
          </div>

          <div className="px-4 py-2.5 bg-red-950/10 sm:text-md text-xs text-gray-400 leading-relaxed">
            <span className="sm:text-md text-xs text-gray-400 font-semibold">Note：</span>
            <span>
              Please select the Test Category first, then select the Test Item, and finally select ODM/Other Brand. After all
              selections are completed, click the
            </span>
            <span className="text-gray-400 font-semibold sm:text-md text-xs">"Test Standard Query"</span> button.
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 px-4 py-3 bg-red-900/20 border border-red-700/50 text-xs text-red-300">
            {error}
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-0 px-6 py-5">
        {queried ? (
          <>
            {rows.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1 text-gray-600 gap-3">
                <ImageOff className="w-10 h-10 text-gray-800" />
                <p className="text-sm">No results found for the selected filters.</p>
              </div>
            ) : (
              <div className="flex flex-col flex-1 min-h-0">
                <p className="sm:text-base text-xs text-white mb-3 shrink-0">
                  {rows.length} record{rows.length !== 1 ? "s" : ""} found
                  {odmBrand && odmBrand !== "All" && (
                    <> · <span className="text-blue-400 border border-white/40 px-1.5 py-0.5">{odmBrand}</span></>
                  )}
                </p>
                <div className="flex-1 overflow-auto border border-white rounded-md bg-[#07090e]">
                  <table className="w-full min-w-[1000px] border border-white/20 text-xs sm:text-sm">

                    {/* HEADER */}
                    <thead>
                      <tr className="text-gray-200 uppercase tracking-wide">
                        {[
                          "Region",
                          "Test Category",
                          "TPV Test Items",
                          "TPV Test Condition",
                          "TPV Test Standard",
                          "Chinese Standard",
                          "ODM / Other Brand",
                          "Test Item",
                          "Test Standard",
                          "Standard Comparison",
                        ].map((title, i) => (
                          <th
                            key={i}
                            className=" sticky top-0 z-20 bg-[#090d14] text-white font-semibold border border-white px-2 py-2 min-w-[120px]">
                            {title}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    {/* BODY */}
                    <tbody className="divide-y divide-white/10">
                      {groupedRows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-white/[0.03] transition-colors">

                          {row._isFirst && (
                            <td rowSpan={row._span} className="border px-2 py-2 text-center bg-[#090d14]">
                              {row.region || "All"}
                            </td>
                          )}

                          {row._isFirst && (
                            <td rowSpan={row._span} className="border border-white px-2 py-2 bg-[#090d14] text-blue-300 font-semibold">
                              {row.testCategory}
                            </td>
                          )}

                          {row._isFirst && (
                            <td rowSpan={row._span} className="border px-2 py-2 bg-[#090d14]">
                              {row.tpvTestItems}
                            </td>
                          )}

                          {row._isFirst && (
                            <td rowSpan={row._span} className="border px-2 py-2">
                              <CellWithImages
                                text={row.tpvTestCondition}
                                imageKeys={row.tpvConditionImageKeys}
                                onImageClick={setActiveImageKey}
                              />
                            </td>
                          )}

                          {row._isFirst && (
                            <td rowSpan={row._span} className="border px-2 py-2">
                              <CellWithImages
                                text={row.tpvTestStandard}
                                imageKeys={row.tpvStandardImageKeys}
                                onImageClick={setActiveImageKey}
                              />
                            </td>
                          )}

                          {row._isFirst && (
                            <td rowSpan={row._span} className="border px-2 py-2 text-center">
                              {row.chineseStandard || "N/A"}
                            </td>
                          )}

                          <td className="border px-2 py-2 bg-[#090d14]">{row.odmBrand}</td>

                          <td className="border px-2 py-2">{row.testItem}</td>

                          <td className="border px-2 py-2">
                            <CellWithImages
                              text={row.testStandard}
                              imageKeys={row.imageKeys}
                              onImageClick={setActiveImageKey}
                            />
                          </td>

                          <td className="border px-2 py-2 text-center">
                            {row.standardComparison}
                          </td>

                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="shrink-0 mt-4 px-4 sm:py-7 py-3 bg-[#0b0f16]/95 backdrop-blur-sm border-t border-gray-500 flex items-center justify-between text-sm text-gray-300 sticky bottom-0 left-0 right-0 z-20">
                  <div className="flex items-center gap-3">
                    {/* Add pagination controls here later if needed */}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          !loading && (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-700 gap-3 pb-20">
              <Search className="w-12 h-12 text-gray-800" />
              <p className="text-sm">
                Select filters and click <span className="text-gray-500">Test Standard Query</span>
              </p>
            </div>
          )
        )}
      </div>
      <ImageModal imageKey={activeImageKey} onClose={() => setActiveImageKey(null)} />
    </div>
  );
}